import { useState } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { ThemeToggle } from '@/components/ThemeToggle'
import { usePostApiUsersPasswordReset } from '@/api/generated/public/public'
import { notify } from '@/lib/notify'
import { isServerUnreachable } from '@/lib/apiError'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/shadcn/card'
import { WallpaperBackground } from '@/pages/login/components/WallpaperBackground'

export default function ForgotPasswordPage() {
  const { t } = useTranslation('login')
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const { mutate: requestReset, isPending } = usePostApiUsersPasswordReset({
    mutation: {
      onSuccess: () => setSubmitted(true),
      onError: (error) => {
        if (isServerUnreachable(error)) {
          notify.error(t('serverUnreachable'))
          return
        }
        notify.apiError(error, t('forgotPasswordPage.errors.requestFailed'))
      },
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    requestReset({ data: { email } })
  }

  return (
    <WallpaperBackground>
      <Card className="w-full max-w-sm">
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-2xl">{t('forgotPasswordPage.title')}</CardTitle>
            <CardDescription>
              {submitted
                ? t('forgotPasswordPage.descriptionSubmitted')
                : t('forgotPasswordPage.description')}
            </CardDescription>
          </div>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <LanguageSwitcher />
          </div>
        </CardHeader>
        <CardContent>
          {submitted ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                {t('forgotPasswordPage.emailSentPrefix')} <strong>{email}</strong> {t('forgotPasswordPage.emailSentSuffix')}
              </p>
              <Link to="/login">
                <Button variant="outline" className="w-full">{t('forgotPasswordPage.backToLogin')}</Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('forgotPasswordPage.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder={t('forgotPasswordPage.placeholder')}
                />
              </div>

              <Button type="submit" disabled={isPending} className="w-full">
                {isPending ? t('forgotPasswordPage.submitting') : t('forgotPasswordPage.submit')}
              </Button>

              <div className="text-center">
                <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">
                  {t('forgotPasswordPage.backToLogin')}
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </WallpaperBackground>
  )
}
